import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Car, Plus, ArrowLeft, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, fmtDate } from "@/lib/api";

const EMPTY_VEH = {
  reg_number: "", make: "", model: "", year: "", color: "",
  fuel_type: "Petrol", odometer: "",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [makes, setMakes] = useState({});

  const [addOpen, setAddOpen] = useState(false);
  const [veh, setVeh] = useState(EMPTY_VEH);
  const [customMake, setCustomMake] = useState(false);
  const [customModel, setCustomModel] = useState(false);

  const [editCustOpen, setEditCustOpen] = useState(false);
  const [custForm, setCustForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });

  const [editVehOpen, setEditVehOpen] = useState(false);
  const [editVehForm, setEditVehForm] = useState(null);
  const [editCustomMake, setEditCustomMake] = useState(false);
  const [editCustomModel, setEditCustomModel] = useState(false);

  const load = () => api.get(`/customers/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/master/vehicle-makes-v2").then((r) => setMakes(r.data || {})).catch(() => setMakes({}));
  }, [id]);

  const addModels = useMemo(() => makes[veh.make] || [], [makes, veh.make]);
  const editModels = useMemo(() => (editVehForm ? (makes[editVehForm.make] || []) : []), [makes, editVehForm]);

  const addVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicles", {
        customer_id: id, ...veh,
        year: veh.year ? parseInt(veh.year) : null,
        odometer: veh.odometer ? parseInt(veh.odometer) : 0,
      });
      toast.success("Vehicle added");
      setAddOpen(false);
      setVeh(EMPTY_VEH);
      setCustomMake(false); setCustomModel(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openEditCustomer = () => {
    setCustForm({
      name: data.name || "", phone: data.phone || "", email: data.email || "",
      address: data.address || "", notes: data.notes || "",
    });
    setEditCustOpen(true);
  };
  const saveCustomer = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/customers/${id}`, custForm);
      toast.success("Customer updated");
      setEditCustOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditVehicle = (v) => {
    setEditVehForm({
      id: v.id, reg_number: v.reg_number || "", make: v.make || "",
      model: v.model || "", year: v.year || "", color: v.color || "",
      fuel_type: v.fuel_type || "Petrol", odometer: v.odometer || "",
      vin: v.vin || "", variant: v.variant || "",
    });
    setEditCustomMake(false); setEditCustomModel(false);
    setEditVehOpen(true);
  };
  const saveVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/vehicles/${editVehForm.id}`, {
        reg_number: editVehForm.reg_number,
        make: editVehForm.make, model: editVehForm.model,
        year: editVehForm.year, variant: editVehForm.variant,
        vin: editVehForm.vin, fuel_type: editVehForm.fuel_type,
        odometer: editVehForm.odometer, color: editVehForm.color,
      });
      toast.success("Vehicle updated");
      setEditVehOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="text-steel">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="customer-detail-root">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-steel hover:text-strawberry">
        <ArrowLeft size={14} /> Back to customers
      </Link>

      <div className="p-6 bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">Customer</div>
            <h1 className="text-3xl font-display font-bold" data-testid="customer-name">{data.name}</h1>
          </div>
          <Button data-testid="customer-edit-btn" onClick={openEditCustomer}
                  className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
            <Pencil size={13} /> Edit customer
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Phone</div>
            <div className="font-mono">{data.phone}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Email</div>
            <div>{data.email || "—"}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase font-mono tracking-widest text-steel">Address</div>
            <div>{data.address || "—"}</div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
        <div className="flex items-center justify-between p-5 border-b border-frosted/50 dark:border-frosted/50">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-steel">Fleet</div>
            <div className="font-display text-lg font-semibold">Vehicles</div>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-btn" className="rounded-none h-10 btn-primary-brand rounded-md font-semibold">
                <Plus size={16} className="mr-1" /> Add vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-display">Add vehicle</DialogTitle></DialogHeader>
              <form onSubmit={addVehicle} className="space-y-3" data-testid="vehicle-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Reg Number</Label>
                    <Input data-testid="vehicle-reg-input" required className="rounded-none mt-1 font-mono uppercase" value={veh.reg_number}
                      onChange={(e) => setVeh({ ...veh, reg_number: e.target.value.toUpperCase() })} />
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Input data-testid="vehicle-year-input" type="number" className="rounded-none mt-1" value={veh.year}
                      onChange={(e) => setVeh({ ...veh, year: e.target.value })} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Make</Label>
                      <button type="button" data-testid="add-vehicle-make-toggle"
                              onClick={() => { setCustomMake(!customMake); setCustomModel(false); setVeh({ ...veh, make: "", model: "" }); }}
                              className="text-[10px] font-semibold uppercase tracking-wider text-strawberry hover:underline">
                        {customMake ? "Pick from list" : "Type in"}
                      </button>
                    </div>
                    {customMake ? (
                      <Input data-testid="vehicle-make-input-custom" required className="rounded-none mt-1" value={veh.make}
                        onChange={(e) => setVeh({ ...veh, make: e.target.value })} />
                    ) : (
                      <select data-testid="vehicle-make-input" required className="rounded-none mt-1 w-full border h-10 px-2 bg-white text-[13px]"
                        value={veh.make} onChange={(e) => setVeh({ ...veh, make: e.target.value, model: "" })}>
                        <option value="">Select…</option>
                        {Object.keys(makes).map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Model</Label>
                      {!customMake && (
                        <button type="button" data-testid="add-vehicle-model-toggle"
                                onClick={() => { setCustomModel(!customModel); setVeh({ ...veh, model: "" }); }}
                                className="text-[10px] font-semibold uppercase tracking-wider text-strawberry hover:underline">
                          {customModel ? "Pick from list" : "Type in"}
                        </button>
                      )}
                    </div>
                    {(customMake || customModel) ? (
                      <Input data-testid="vehicle-model-input-custom" required className="rounded-none mt-1" value={veh.model}
                        onChange={(e) => setVeh({ ...veh, model: e.target.value })} />
                    ) : (
                      <select data-testid="vehicle-model-input" required className="rounded-none mt-1 w-full border h-10 px-2 bg-white text-[13px]"
                        value={veh.model} onChange={(e) => setVeh({ ...veh, model: e.target.value })}>
                        <option value="">Select…</option>
                        {addModels.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input className="rounded-none mt-1" value={veh.color}
                      onChange={(e) => setVeh({ ...veh, color: e.target.value })} />
                  </div>
                  <div>
                    <Label>Odometer (km)</Label>
                    <Input data-testid="vehicle-odo-input" type="number" className="rounded-none mt-1" value={veh.odometer}
                      onChange={(e) => setVeh({ ...veh, odometer: e.target.value })} />
                  </div>
                </div>
                <Button type="submit" data-testid="vehicle-save-btn" className="w-full h-11 btn-primary-brand rounded-md text-white font-semibold">
                  Save vehicle
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <table className="w-full table-industrial">
          <thead>
            <tr><th>Reg</th><th>Make/Model</th><th>Year</th><th>Fuel</th><th>Odo (km)</th><th>Added</th><th></th></tr>
          </thead>
          <tbody>
            {(data.vehicles || []).length === 0 && (
              <tr><td colSpan={7} className="text-center text-steel py-8">No vehicles yet.</td></tr>
            )}
            {(data.vehicles || []).map((v) => (
              <tr key={v.id}>
                <td className="font-mono text-strawberry">{v.reg_number}</td>
                <td className="flex items-center gap-2"><Car size={14} className="text-steel/80" />{v.make} {v.model}</td>
                <td className="font-mono text-sm">{v.year || "—"}</td>
                <td className="text-sm">{v.fuel_type || "—"}</td>
                <td className="font-mono text-sm text-right">{v.odometer || 0}</td>
                <td className="font-mono text-xs text-steel">{fmtDate(v.created_at)}</td>
                <td className="text-right">
                  <button type="button" onClick={() => openEditVehicle(v)}
                          data-testid={`edit-vehicle-btn-${v.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]">
                    <Pencil size={11} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Customer Dialog */}
      <Dialog open={editCustOpen} onOpenChange={setEditCustOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader><DialogTitle className="font-semibold text-[#1d3557]">Edit customer</DialogTitle></DialogHeader>
          <form onSubmit={saveCustomer} className="space-y-3" data-testid="edit-customer-form">
            <div>
              <Label className="text-[12px] font-semibold text-[#1d3557]">Name</Label>
              <Input data-testid="edit-customer-name" required className="rounded-md mt-1" value={custForm.name}
                onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557]">Phone</Label>
                <Input data-testid="edit-customer-phone" required className="rounded-md mt-1" value={custForm.phone}
                  onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557]">Email</Label>
                <Input data-testid="edit-customer-email" type="email" className="rounded-md mt-1" value={custForm.email}
                  onChange={(e) => setCustForm({ ...custForm, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[12px] font-semibold text-[#1d3557]">Address</Label>
              <Textarea data-testid="edit-customer-address" className="rounded-md mt-1" rows={2} value={custForm.address}
                onChange={(e) => setCustForm({ ...custForm, address: e.target.value })} />
            </div>
            <div>
              <Label className="text-[12px] font-semibold text-[#1d3557]">Notes</Label>
              <Textarea data-testid="edit-customer-notes" className="rounded-md mt-1" rows={2} value={custForm.notes}
                onChange={(e) => setCustForm({ ...custForm, notes: e.target.value })} />
            </div>
            <Button data-testid="edit-customer-save-btn" type="submit" className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold">
              Save changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={editVehOpen} onOpenChange={setEditVehOpen}>
        <DialogContent className="rounded-lg max-w-lg">
          <DialogHeader><DialogTitle className="font-semibold text-[#1d3557]">Edit vehicle</DialogTitle></DialogHeader>
          {editVehForm && (
            <form onSubmit={saveVehicle} className="space-y-3" data-testid="edit-vehicle-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Reg Number</Label>
                  <Input data-testid="edit-vehicle-reg" required className="rounded-md mt-1 font-mono uppercase"
                         value={editVehForm.reg_number}
                         onChange={(e) => setEditVehForm({ ...editVehForm, reg_number: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Year</Label>
                  <Input data-testid="edit-vehicle-year" type="number" className="rounded-md mt-1"
                         value={editVehForm.year}
                         onChange={(e) => setEditVehForm({ ...editVehForm, year: e.target.value })} />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-[12px] font-semibold text-[#1d3557]">Make</Label>
                    <button type="button" data-testid="edit-vehicle-make-toggle"
                            onClick={() => { setEditCustomMake(!editCustomMake); setEditCustomModel(false); setEditVehForm({ ...editVehForm, make: "", model: "" }); }}
                            className="text-[10px] font-semibold uppercase tracking-wider text-strawberry hover:underline">
                      {editCustomMake ? "Pick from list" : "Type in"}
                    </button>
                  </div>
                  {editCustomMake ? (
                    <Input data-testid="edit-vehicle-make-custom" required className="rounded-md mt-1" value={editVehForm.make}
                           onChange={(e) => setEditVehForm({ ...editVehForm, make: e.target.value })} />
                  ) : (
                    <select data-testid="edit-vehicle-make" required
                            className="rounded-md mt-1 w-full border h-10 px-2 bg-white text-[13px]"
                            value={editVehForm.make}
                            onChange={(e) => setEditVehForm({ ...editVehForm, make: e.target.value, model: "" })}>
                      <option value="">Select…</option>
                      {Object.keys(makes).map((m) => <option key={m} value={m}>{m}</option>)}
                      {editVehForm.make && !Object.keys(makes).includes(editVehForm.make) &&
                        <option value={editVehForm.make}>{editVehForm.make} (custom)</option>}
                    </select>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-[12px] font-semibold text-[#1d3557]">Model</Label>
                    {!editCustomMake && (
                      <button type="button" data-testid="edit-vehicle-model-toggle"
                              onClick={() => { setEditCustomModel(!editCustomModel); setEditVehForm({ ...editVehForm, model: "" }); }}
                              className="text-[10px] font-semibold uppercase tracking-wider text-strawberry hover:underline">
                        {editCustomModel ? "Pick from list" : "Type in"}
                      </button>
                    )}
                  </div>
                  {(editCustomMake || editCustomModel) ? (
                    <Input data-testid="edit-vehicle-model-custom" required className="rounded-md mt-1" value={editVehForm.model}
                           onChange={(e) => setEditVehForm({ ...editVehForm, model: e.target.value })} />
                  ) : (
                    <select data-testid="edit-vehicle-model" required
                            className="rounded-md mt-1 w-full border h-10 px-2 bg-white text-[13px]"
                            value={editVehForm.model}
                            onChange={(e) => setEditVehForm({ ...editVehForm, model: e.target.value })}>
                      <option value="">Select…</option>
                      {editModels.map((m) => <option key={m} value={m}>{m}</option>)}
                      {editVehForm.model && !editModels.includes(editVehForm.model) &&
                        <option value={editVehForm.model}>{editVehForm.model} (custom)</option>}
                    </select>
                  )}
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Fuel</Label>
                  <select data-testid="edit-vehicle-fuel" className="rounded-md mt-1 w-full border h-10 px-2 bg-white text-[13px]"
                    value={editVehForm.fuel_type}
                    onChange={(e) => setEditVehForm({ ...editVehForm, fuel_type: e.target.value })}>
                    {["Petrol", "Diesel", "CNG", "Petrol+CNG", "Electric", "Hybrid", "LPG"].map((f) =>
                      <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Color</Label>
                  <Input className="rounded-md mt-1" value={editVehForm.color}
                         onChange={(e) => setEditVehForm({ ...editVehForm, color: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Odometer</Label>
                  <Input data-testid="edit-vehicle-odo" type="number" className="rounded-md mt-1" value={editVehForm.odometer}
                         onChange={(e) => setEditVehForm({ ...editVehForm, odometer: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">VIN</Label>
                  <Input className="rounded-md mt-1 font-mono" value={editVehForm.vin}
                         onChange={(e) => setEditVehForm({ ...editVehForm, vin: e.target.value })} />
                </div>
              </div>
              <Button data-testid="edit-vehicle-save-btn" type="submit" className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold">
                Save changes
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
